import React, { useId, useState } from 'react';
import type { ReactElement, ReactNode, SyntheticEvent } from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { removeDialog } from 'app-components/flux/root-ui/rootUiActions';

import StdDialog, { isDialogCloseAction, type StdDialogCloseReason } from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import { useAppDispatch } from 'hooks/redux';

import useStyles from './styles';
import type {
  ConfirmationDialogAction,
  ConfirmationDialogButton,
  ConfirmationDialogProps,
  ConfirmationDialogReason,
} from './types';

export { DIALOG_TYPE, mkConfirmationDialogProps } from './types';

const CONTENT_VIEWS = new Map<string, ReactNode>();

export const registerContentView = (key: string, view: ReactNode): void => {
  CONTENT_VIEWS.set(key, view);
};

type Props = ConfirmationDialogProps & {
  dialogId?: string;
  type?: string;
};

const ConfirmationDialog = (props: Props): ReactElement => {
  const {
    confirmAction,
    confirmButtonVariant,
    confirmLabel,
    confirmStartIcon,
    content,
    contentViewId,
    denyAction,
    denyButtonVariant,
    denyLabel,
    dialogCloseAliasAction,
    dialogId,
    hideBackdrop,
    onClose,
    showCloseButton = true,
    size,
    tertiaryAction,
    tertiaryLabel,
    tertiaryLabelVariant,
    textEntryConfirmationText,
    title,
    unregisterContentViewOnClose,
    ...otherProps
  } = props;

  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const generatedTitleId = useId();
  const titleId = `confirmation-dialog-${dialogId ?? generatedTitleId}-title`;
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(Boolean(textEntryConfirmationText));

  const handleClose = (event: SyntheticEvent | null, reason: ConfirmationDialogReason): void => {
    const accepted = onClose?.(event, reason);

    // Closing with the X, Escape or the backdrop counts as the alias button.
    const isPressed = (button: ConfirmationDialogButton): boolean =>
      reason === button || (dialogCloseAliasAction === button && isDialogCloseAction(reason));
    const runAction = (action: ConfirmationDialogAction | null | undefined): void => {
      if (typeof action === 'function') {
        action(reason, event);
      } else if (action) {
        // Untyped callers can pass a Redux action instead.
        dispatch(action);
      }
    };

    if (isPressed('CONFIRM')) {
      runAction(confirmAction);
    }
    if (isPressed('DENY')) {
      runAction(denyAction);
    }
    if (isPressed('APPLY_TO_ALL')) {
      runAction(tertiaryAction);
    }

    if (accepted !== false) {
      dispatch(removeDialog(dialogId));

      if (contentViewId && unregisterContentViewOnClose) {
        CONTENT_VIEWS.delete(contentViewId);
      }
    }
  };

  const isString = typeof content === 'string';
  return (
    <StdDialog
      aria-labelledby={titleId}
      // Without a stdDialogRef, StdDialog reports only its own close reasons.
      onClose={(event, reason) => handleClose(event, reason as StdDialogCloseReason)}
      open
      sharedcomponentid='DIALOG_CONFIRMATION'
      showCloseButton={showCloseButton}
      title={title}
      titleProps={{ titleId }}
      hideBackdrop={hideBackdrop}
      size={size || 'sm'}
      dialogId={dialogId}
      {...otherProps}
    >
      <DialogContent>
        {content && isString && (
          <Typography variant='bodyRegular' id='confirmation-dialog-description' sx={{ whiteSpace: 'pre-line' }}>
            {content}
          </Typography>
        )}
        {!isString && content}

        {contentViewId && CONTENT_VIEWS.get(contentViewId)}
        {textEntryConfirmationText && (
          <TextField
            autoFocus
            className={classes.confirmationTextField}
            id='text-confirmation'
            label={`Type ${textEntryConfirmationText} to confirm`}
            onChange={(event) => setConfirmButtonDisabled(event.target.value !== textEntryConfirmationText)}
          />
        )}
      </DialogContent>
      <DialogActions>
        {tertiaryLabel && (
          <QButton
            className={classes.tertiary}
            variant={tertiaryLabelVariant || 'delete'}
            id={`confirm-${tertiaryLabel}`}
            disableOutline
            onClick={(event?: SyntheticEvent) => handleClose(event ?? null, 'APPLY_TO_ALL')}
          >
            {tertiaryLabel}
          </QButton>
        )}
        {denyLabel && (
          <QButton
            variant={denyButtonVariant || 'outlined'}
            id={`confirm-${denyLabel}`}
            onClick={(event?: SyntheticEvent) => handleClose(event ?? null, 'DENY')}
            color='primary'
          >
            {denyLabel}
          </QButton>
        )}
        <QButton
          variant={confirmButtonVariant || 'contained'}
          id={`confirm-${confirmLabel}`}
          disabled={confirmButtonDisabled}
          onClick={(event?: SyntheticEvent) => handleClose(event ?? null, 'CONFIRM')}
          startIcon={confirmStartIcon ?? null}
        >
          {confirmLabel}
        </QButton>
      </DialogActions>
    </StdDialog>
  );
};

export default React.memo(ConfirmationDialog);
